import s from '../pages.scss';
import React, { useEffect } from 'react';
import { Box, Page } from '@wix/design-system';
import { PageHeader } from '@wix/app-market-components';
import {
  useEssentials,
  useJunkYard,
  useRouterContext,
  useServices,
} from '../../contexts';
import { QueryDecorator } from '../../decorators';
import { getCollectionPageData } from './collection-page-data';
import type { CollectionPageData } from './collection-page-data';
import { AppMarketPage } from '../../enums';
import { getPageViewBiData } from '../../bi-services/events-data-parser';
import {
  appMarketBackButtonClick,
  appMarketPageView,
  appMarketPageStartLoadedSrc24Evid163,
} from '@wix/bi-logger-app-market-data/v2';
import { RoutePath, type RoutePayloadMap } from '../../models';
import { CollectionSkeleton } from './collection-skeleton';
import { BackButton } from '../../components/back-button';
import { withSaleBanner } from '../../components/sale-banner';
import { CollectionComponent } from './collection-component';
import { queryKeyBuilder } from '../../components/apps-section/app-section-helper';

function CollectionPageComponent({
  slug,
  primaryTitle,
  secondaryTitle,
  description,
  appGroup,
  paging,
}: CollectionPageData) {
  const { routerData } = useJunkYard();
  const { biLogger } = useEssentials();
  const { router } = useRouterContext();
  const page = AppMarketPage.COLLECTION;

  function onBack() {
    biLogger.report(
      appMarketBackButtonClick({
        tag_type: page,
        tag_name: slug,
        location: 'collection_page',
      }),
    );
    router.navigateTo({
      path: RoutePath.BACK,
      payload: undefined,
    });
  }

  useEffect(() => {
    const urlParams = location.pathname.split('/')[3];
    biLogger.report(
      appMarketPageView(
        getPageViewBiData({
          routerData,
          urlParams,
          pageType: page,
          pageName: slug,
        }),
      ),
    );
  }, []);

  return (
    <Page className={`${s.appMarketPage} ${s.appMarketPageBackgroundColor}`}>
      <Page.Content>
        {router.hasHistory() && (
          <Box marginTop="18px">
            <BackButton
              onBack={onBack}
              size="small"
              weight="thin"
              skin="dark"
            />
          </Box>
        )}
        <Box
          dataHook="collection-page-header"
          marginTop={router.hasHistory() ? '6px' : '18px'}
        >
          <PageHeader
            primaryTitle={secondaryTitle}
            secondaryTitle={primaryTitle}
            description={description}
          />
        </Box>
        <CollectionComponent slug={slug} initData={{ appGroup, paging }} />
      </Page.Content>
    </Page>
  );
}

export const CollectionPage = withSaleBanner(function ({
  slug,
}: {
  slug: string;
}) {
  const { biLogger, i18n, experiments } = useEssentials();
  const { route } = useRouterContext();
  const services = useServices();
  const isSale = Boolean(
    (route.payload as RoutePayloadMap[RoutePath.COLLECTION]).isSale,
  );

  useEffect(() => {
    biLogger.report(
      appMarketPageStartLoadedSrc24Evid163({
        tag_type: AppMarketPage.COLLECTION,
      }),
    );
  }, [slug]);

  return (
    <QueryDecorator
      queryFn={() =>
        getCollectionPageData({
          services,
          experiments,
          slug,
          languageCode: i18n.language,
          isSale,
        })
      }
      queryKey={queryKeyBuilder({
        queryName: 'initial-apps-fetch',
        language: i18n.language,
        isSale,
        appsLength: 0,
        queryId: slug,
      })}
    >
      {({ data: collectionPageData, isLoading }) =>
        isLoading ? (
          <CollectionSkeleton />
        ) : (
          <CollectionPageComponent {...collectionPageData} />
        )
      }
    </QueryDecorator>
  );
});
